import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ja } from 'date-fns/locale/ja';
import { SnackbarProvider } from 'notistack';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useRoutes } from 'react-router';
import SelectedCompanyForbiddenDialog from 'src/components/SelectedCompanyForbiddenDialog';
import { TruckShiftUpdateIndicator } from 'src/components/TruckShiftUpdateIndicator';
import { TruckShiftUpdateProvider } from 'src/contexts/TruckShiftUpdateContext';
import { invalidateTruckShiftQueries } from 'src/hooks/invalidateTruckShiftQueries';
import { TRUCK_SHIFT_UPDATE_MUTATION_KEY } from 'src/hooks/useIsTruckShiftUpdating';
import router from 'src/router';
import ThemeProvider from 'src/theme/ThemeProvider';
import { setupMuiX } from 'src/utils/setupMuiX';

if (ja && ja.options) {
  ja.options.weekStartsOn = 1;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
setupMuiX(import.meta.env.VITE_MUI_LICENSE);

const queryOptions = {
  defaultOptions: {
    queries: {
      // retry: false,
      // refetchOnMount: true,
      // suspense: true,
    },
  },
};
// 稼働表更新 mutation（TRUCK_SHIFT_UPDATE_MUTATION_KEY）の成功時に関連クエリを invalidate する。
// hook の onSuccess ではなく MutationCache に集約することで、mutation を開始した画面から離れて
// （＝ その hook が unmount して）も、完了時に確実に再取得される。
const queryClient = new QueryClient({
  ...queryOptions,
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.options.mutationKey?.[0] === TRUCK_SHIFT_UPDATE_MUTATION_KEY[0]) {
        invalidateTruckShiftQueries(queryClient);
      }
    },
  }),
});

function App() {
  const content = useRoutes(router);

  useEffect(() => {
    if (!ReactGA.isInitialized) {
      ReactGA.initialize('G-MGYSVEJN30');
    }
  }, []);

  useEffect(() => {
    const page = window.location.href;
    ReactGA.send({ hitType: 'pageview', page });
  }, [content]);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TruckShiftUpdateProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
            <SnackbarProvider
              maxSnack={5}
              preventDuplicate
              dense
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
            >
              <CssBaseline />
              {content}
              <TruckShiftUpdateIndicator />
              <SelectedCompanyForbiddenDialog />
            </SnackbarProvider>
          </LocalizationProvider>
        </TruckShiftUpdateProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
